import React from 'react';
import { Card } from '../ui/card';

interface KundliChartProps {
  data: any;
}

export function KundliChart({ data }: KundliChartProps) {
  const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  
  const signSymbols = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];

  // North Indian style chart layout (Diamond shape)
  const chartLayout = [
    [11, 0, 1],
    [10, -1, 2],
    [9, 8, 3],
    [-1, 7, 4],
    [-1, 6, 5]
  ];

  const getHouseContent = (houseNum: number) => {
    if (houseNum === -1) return null;
    
    const house = data.houses[houseNum];
    const planetsInHouse = data.planetaryPositions.filter((p: any) => p.house === houseNum + 1);
    
    return (
      <div className="flex flex-col items-center justify-center h-full p-2">
        <div className="text-xs font-semibold text-gray-600 mb-1">
          {houseNum + 1}
        </div>
        <div className="text-sm font-medium text-orange-600 mb-1">
          {signSymbols[signs.indexOf(house.sign)]} {house.sign.slice(0, 3)}
        </div>
        <div className="flex flex-wrap gap-1 justify-center">
          {planetsInHouse.map((planet: any, idx: number) => (
            <span
              key={idx}
              className={`text-xs font-semibold ${planet.color}`}
              title={`${planet.name} ${planet.degree}°`}
            >
              {planet.icon}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Basic Info */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-br from-orange-50 to-rose-50 border-orange-200">
          <div className="text-sm text-gray-600 mb-1">Sun Sign (Rashi)</div>
          <div className="text-2xl font-bold text-orange-600">{data.predictions.sunSign}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
          <div className="text-sm text-gray-600 mb-1">Moon Sign</div>
          <div className="text-2xl font-bold text-blue-600">{data.predictions.moonSign}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <div className="text-sm text-gray-600 mb-1">Ascendant (Lagna)</div>
          <div className="text-2xl font-bold text-purple-600">{data.predictions.ascendant}</div>
        </Card>
      </div>

      {/* Birth Chart - North Indian Style */}
      <div className="flex justify-center">
        <div className="w-full max-w-2xl">
          <h3 className="text-xl font-bold text-center mb-4 text-gray-900">Birth Chart (Lagna Kundli)</h3>
          <div className="aspect-square bg-gradient-to-br from-orange-100 to-rose-100 p-4 rounded-lg shadow-lg">
            <div className="relative w-full h-full">
              {/* Diamond shaped chart */}
              <svg viewBox="0 0 400 400" className="w-full h-full">
                {/* Outer diamond border */}
                <path
                  d="M 200 20 L 380 200 L 200 380 L 20 200 Z"
                  fill="white"
                  stroke="#f97316"
                  strokeWidth="3"
                />
                
                {/* Horizontal lines */}
                <line x1="200" y1="20" x2="200" y2="380" stroke="#f97316" strokeWidth="2" />
                <line x1="20" y1="200" x2="380" y2="200" stroke="#f97316" strokeWidth="2" />
                
                {/* Diagonal lines */}
                <line x1="110" y1="110" x2="290" y2="290" stroke="#f97316" strokeWidth="2" />
                <line x1="110" y1="290" x2="290" y2="110" stroke="#f97316" strokeWidth="2" />
              </svg>

              {/* House positions */}
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
                {/* Row 1 */}
                <div className="border-r border-b border-orange-300 relative">
                  <div className="absolute inset-0 flex items-start justify-end p-2">
                    <div className="text-center">
                      {data.houses[11] && (
                        <>
                          <div className="text-xs font-bold text-gray-700">12</div>
                          <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[11].sign)]}</div>
                          <div className="flex gap-1 flex-wrap justify-center mt-1">
                            {data.planetaryPositions
                              .filter((p: any) => p.house === 12)
                              .map((p: any, i: number) => (
                                <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="border-b border-orange-300 bg-orange-50/50 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      {data.houses[0] && (
                        <>
                          <div className="text-xs font-bold text-gray-700">1 (ASC)</div>
                          <div className="text-sm font-bold text-orange-600">{signSymbols[signs.indexOf(data.houses[0].sign)]}</div>
                          <div className="flex gap-1 flex-wrap justify-center mt-1">
                            {data.planetaryPositions
                              .filter((p: any) => p.house === 1)
                              .map((p: any, i: number) => (
                                <span key={i} className={`text-sm font-bold ${p.color}`}>{p.icon}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="border-l border-b border-orange-300 relative">
                  <div className="absolute inset-0 flex items-start justify-start p-2">
                    <div className="text-center">
                      {data.houses[1] && (
                        <>
                          <div className="text-xs font-bold text-gray-700">2</div>
                          <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[1].sign)]}</div>
                          <div className="flex gap-1 flex-wrap justify-center mt-1">
                            {data.planetaryPositions
                              .filter((p: any) => p.house === 2)
                              .map((p: any, i: number) => (
                                <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 2 */}
                <div className="border-r border-orange-300 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      {data.houses[10] && (
                        <>
                          <div className="text-xs font-bold text-gray-700">11</div>
                          <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[10].sign)]}</div>
                          <div className="flex gap-1 flex-wrap justify-center mt-1">
                            {data.planetaryPositions
                              .filter((p: any) => p.house === 11)
                              .map((p: any, i: number) => (
                                <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-orange-200 to-rose-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-sm font-bold text-gray-700">Birth Chart</div>
                    <div className="text-xs text-gray-600 mt-1">{data.basicDetails.name}</div>
                  </div>
                </div>
                
                <div className="border-l border-orange-300 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      {data.houses[2] && (
                        <>
                          <div className="text-xs font-bold text-gray-700">3</div>
                          <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[2].sign)]}</div>
                          <div className="flex gap-1 flex-wrap justify-center mt-1">
                            {data.planetaryPositions
                              .filter((p: any) => p.house === 3)
                              .map((p: any, i: number) => (
                                <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 3 */}
                <div className="border-r border-t border-orange-300 relative">
                  <div className="absolute inset-0 flex items-end justify-end p-2">
                    <div className="text-center">
                      {data.houses[9] && (
                        <>
                          <div className="text-xs font-bold text-gray-700">10</div>
                          <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[9].sign)]}</div>
                          <div className="flex gap-1 flex-wrap justify-center mt-1">
                            {data.planetaryPositions
                              .filter((p: any) => p.house === 10)
                              .map((p: any, i: number) => (
                                <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-orange-300 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      {data.houses[8] && (
                        <>
                          <div className="text-xs font-bold text-gray-700">9</div>
                          <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[8].sign)]}</div>
                          <div className="flex gap-1 flex-wrap justify-center mt-1">
                            {data.planetaryPositions
                              .filter((p: any) => p.house === 9)
                              .map((p: any, i: number) => (
                                <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="border-l border-t border-orange-300 relative">
                  <div className="absolute inset-0 flex items-end justify-start p-2">
                    <div className="text-center">
                      {data.houses[3] && (
                        <>
                          <div className="text-xs font-bold text-gray-700">4</div>
                          <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[3].sign)]}</div>
                          <div className="flex gap-1 flex-wrap justify-center mt-1">
                            {data.planetaryPositions
                              .filter((p: any) => p.house === 4)
                              .map((p: any, i: number) => (
                                <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Remaining houses in corners */}
              <div className="absolute" style={{ top: '75%', left: '5%' }}>
                <div className="text-center">
                  {data.houses[7] && (
                    <>
                      <div className="text-xs font-bold text-gray-700">8</div>
                      <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[7].sign)]}</div>
                      <div className="flex gap-1 flex-wrap justify-center mt-1">
                        {data.planetaryPositions
                          .filter((p: any) => p.house === 8)
                          .map((p: any, i: number) => (
                            <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="absolute" style={{ bottom: '35%', left: '5%' }}>
                <div className="text-center">
                  {data.houses[6] && (
                    <>
                      <div className="text-xs font-bold text-gray-700">7</div>
                      <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[6].sign)]}</div>
                      <div className="flex gap-1 flex-wrap justify-center mt-1">
                        {data.planetaryPositions
                          .filter((p: any) => p.house === 7)
                          .map((p: any, i: number) => (
                            <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="absolute" style={{ bottom: '10%', left: '42%' }}>
                <div className="text-center">
                  {data.houses[5] && (
                    <>
                      <div className="text-xs font-bold text-gray-700">6</div>
                      <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[5].sign)]}</div>
                      <div className="flex gap-1 flex-wrap justify-center mt-1">
                        {data.planetaryPositions
                          .filter((p: any) => p.house === 6)
                          .map((p: any, i: number) => (
                            <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="absolute" style={{ bottom: '35%', right: '5%' }}>
                <div className="text-center">
                  {data.houses[4] && (
                    <>
                      <div className="text-xs font-bold text-gray-700">5</div>
                      <div className="text-xs text-orange-600">{signSymbols[signs.indexOf(data.houses[4].sign)]}</div>
                      <div className="flex gap-1 flex-wrap justify-center mt-1">
                        {data.planetaryPositions
                          .filter((p: any) => p.house === 5)
                          .map((p: any, i: number) => (
                            <span key={i} className={`text-xs ${p.color}`}>{p.icon}</span>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.planetaryPositions.map((planet: any, index: number) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-lg border">
                <span className={`text-xl ${planet.color}`}>{planet.icon}</span>
                <div>
                  <div className="text-sm font-semibold">{planet.name}</div>
                  <div className="text-xs text-gray-600">{planet.sign} {planet.degree}°</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
